import React from "react";
import { OptionType } from "@atlaskit/select";

import { Select } from "../../UI/Select";
import { InputLabel } from "../../UI/InputLabel";
import { Box } from "../../UI/Box";
import useAuth from "../../routes/login/AuthContext";
import { useSnackbar } from "../../hooks/useSnackbar";
import {
  WeeklyReportDay,
  getWeeklyReportDay,
  setWeeklyReportDay,
} from "../../services/AdminApi";

const DAYS: WeeklyReportDay[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const formatLabel = (day: WeeklyReportDay) =>
  day.charAt(0) + day.slice(1).toLowerCase();

const OPTIONS: OptionType[] = DAYS.map((day) => ({
  value: day,
  label: formatLabel(day),
}));

interface IProps {
  l1Id: number | undefined;
}

export const WeeklyReportSchedule: React.FC<IProps> = ({ l1Id }) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [value, setValue] = React.useState<OptionType | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!l1Id) {
      setValue(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const day = await getWeeklyReportDay(l1Id, await getAuthToken());
      if (cancelled) return;
      if (day) {
        setValue({ value: day, label: formatLabel(day) });
      } else {
        setValue(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [l1Id]);

  const handleChange = async (selection: OptionType | null) => {
    if (!l1Id || !selection) {
      return;
    }
    setIsLoading(true);
    const ok = await setWeeklyReportDay(
      l1Id,
      selection.value as WeeklyReportDay,
      await getAuthToken(),
    );
    if (ok) {
      setValue(selection);
      showSuccessSnackbar("Saved Changes");
    } else {
      showErrorSnackbar("An Error Occurred");
    }
    setIsLoading(false);
  };

  return (
    <Box mb={2}>
      <InputLabel>WEEKLY REPORT SCHEDULE</InputLabel>
      <Select
        inputId="weekly-report-schedule"
        value={value}
        options={OPTIONS}
        onChange={handleChange as any}
        placeholder="Select..."
        isDisabled={!l1Id || isLoading || isSuperAdminRead()}
      />
    </Box>
  );
};
